import {BootMessageDataTypesEnum} from '#/helpers/iframe/courier/types';
import {
  PAGE_DETAILS_MAX_FULL_URL_LENGTH,
  PAGE_DETAILS_MAX_LOCALE_LENGTH,
  PAGE_DETAILS_MAX_REFERRER_LENGTH,
  PAGE_DETAILS_MAX_TIMEZONE_LENGTH,
  PAGE_DETAILS_MAX_TITLE_LENGTH,
  PAGE_DETAILS_MAX_USER_AGENT_LENGTH,
} from '#/helpers/pageDetailsLimits';

/*
 * Constants.
 */

const HOST_NAVIGATION_POLL_INTERVAL_MS = 500;

/*
 * Types.
 */

export interface PageDetailsBootToolWirePayload {
  readonly type: typeof BootMessageDataTypesEnum.PAGE_DETAILS;
  readonly full_url: string;
  readonly title: string;
  readonly referrer?: string;
  readonly user_agent?: string;
  readonly locale?: string;
  readonly timezone?: string;
  readonly pageLoadTimestampMs: number;
}

/*
 * Tool.
 */

/**
 * Reads the host page snapshot used for Companion `client_context.page_details` auto-collection.
 * Sends the full URL (`window.location.href`) so the agent can tell which page the user is on.
 * When `document.referrer` is a parseable absolute URL, sends `origin + pathname` only (no query or hash).
 * Otherwise omits `referrer`. Runs in the parent window (boot script), not inside the iframe.
 */
export function buildPageDetailsBootToolPayload(): PageDetailsBootToolWirePayload {
  const fullUrl = truncateForWire(window.location.href, PAGE_DETAILS_MAX_FULL_URL_LENGTH);
  const title = truncateForWire(document.title, PAGE_DETAILS_MAX_TITLE_LENGTH);
  const referrer = parseReferrerForWire(document.referrer);
  const userAgent = parseUserAgentForWire(navigator.userAgent);
  const locale = parseLocaleForWire(navigator.language);
  const timezone = parseTimezoneForWire();
  const pageLoadTimestampMs = Date.now();

  return {
    type: BootMessageDataTypesEnum.PAGE_DETAILS,
    full_url: fullUrl,
    title,
    ...(referrer !== undefined ? {referrer} : {}),
    ...(userAgent !== undefined ? {user_agent: userAgent} : {}),
    ...(locale !== undefined ? {locale} : {}),
    ...(timezone !== undefined ? {timezone} : {}),
    pageLoadTimestampMs,
  };
}

/**
 * Subscribes to host page navigations so `page_details` can stay in sync. Polls
 * `location.href` rather than patching `history.pushState`/`replaceState` — mutating
 * globals on the host page risks breaking customer routers and any other library
 * that wraps history.
 *
 * Returns a teardown function (call on `FrontCompanion.shutdown`).
 */
export function installPageDetailsBootToolListeners(onHostNavigation: () => void): () => void {
  let lastHref = window.location.href;

  const checkForHostNavigation = () => {
    const currentHref = window.location.href;
    if (currentHref === lastHref) {
      return;
    }
    lastHref = currentHref;
    onHostNavigation();
  };

  window.addEventListener('popstate', checkForHostNavigation);
  const pollIntervalId = window.setInterval(checkForHostNavigation, HOST_NAVIGATION_POLL_INTERVAL_MS);

  return () => {
    window.removeEventListener('popstate', checkForHostNavigation);
    window.clearInterval(pollIntervalId);
  };
}

/*
 * Helpers.
 */

function truncateForWire(value: string, maxLength: number): string {
  if (value.length <= maxLength) {
    return value;
  }
  return value.slice(0, maxLength);
}

/** `origin + pathname` only, or `undefined` if empty or not a valid absolute URL. */
function parseReferrerForWire(raw: string): string | undefined {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return undefined;
  }

  try {
    const url = new URL(trimmed);
    const base = `${url.origin}${url.pathname}`;
    return truncateForWire(base, PAGE_DETAILS_MAX_REFERRER_LENGTH);
  } catch {
    return undefined;
  }
}

function parseUserAgentForWire(rawUserAgent: string | undefined): string | undefined {
  if (rawUserAgent === undefined || rawUserAgent.length === 0) {
    return undefined;
  }
  return truncateForWire(rawUserAgent, PAGE_DETAILS_MAX_USER_AGENT_LENGTH);
}

function parseLocaleForWire(rawLocale: string | undefined): string | undefined {
  if (rawLocale === undefined || rawLocale.length === 0) {
    return undefined;
  }
  return truncateForWire(rawLocale, PAGE_DETAILS_MAX_LOCALE_LENGTH);
}

function parseTimezoneForWire(): string | undefined {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone === undefined || timezone.length === 0) {
      return undefined;
    }
    return truncateForWire(timezone, PAGE_DETAILS_MAX_TIMEZONE_LENGTH);
  } catch {
    return undefined;
  }
}
